<div class="cd-page">
  <app-counselor-nav></app-counselor-nav>

  <main class="cd-main" aria-label="My posts">
    <div class="cd-header">
      <div>
        <h1 class="cd-title">My Posts</h1>
        <p class="cd-subtitle">View and manage your posts and student engagement</p>
      </div>
      <button
        class="cd-btn-primary"
        data-testid="create-post-btn"
        (click)="openCreatePostDialog()"
        aria-label="Create new post"
      >
        <span class="cd-btn-icon" aria-hidden="true">+</span> New Post
      </button>
    </div>

    <div class="cd-card cd-filter-bar" role="search" aria-label="Filter posts">
      <div class="cd-filter-field">
        <label class="cd-filter-label" for="search-posts">Search posts</label>
        <input
          id="search-posts"
          class="cd-filter-input"
          matInput
          [(ngModel)]="searchQuery"
          (ngModelChange)="onSearch($event)"
          (keydown.escape)="clearSearch()"
          placeholder="Search posts…"
          aria-label="Search posts"
          autocomplete="off"
          data-testid="search-posts-input"
        />
      </div>

      <div class="cd-filter-field">
        <label class="cd-filter-label" id="grade-filter-label">Grade levels</label>
        <mat-form-field appearance="outline" class="cd-select-field" subscriptSizing="dynamic">
          <mat-select
            id="grade-filter"
            multiple
            placeholder="All grades"
            [(ngModel)]="selectedGrades"
            (ngModelChange)="onGradeFilterChange()"
            aria-labelledby="grade-filter-label"
            data-testid="grade-filter-select"
          >
            @for (g of gradeOptions; track g.value) {
              <mat-option [value]="g.value" [attr.data-testid]="'grade-option-' + g.value">{{
                g.label
              }}</mat-option>
            }
          </mat-select>
        </mat-form-field>
      </div>

      <div class="cd-filter-field">
        <label class="cd-filter-label" id="post-status-filter-label">Status</label>
        <mat-chip-listbox
          [(ngModel)]="activeFilter"
          hideSingleSelectionIndicator
          aria-labelledby="post-status-filter-label"
          data-testid="post-status-filter-listbox"
        >
          @for (f of filters; track f) {
            <mat-chip-option
              [value]="f"
              [selected]="activeFilter === f"
              (click)="activeFilter = f; onStatusFilterChange()"
              [attr.data-testid]="'post-status-filter-' + f.toLowerCase()"
            >
              {{ f }}
            </mat-chip-option>
          }
        </mat-chip-listbox>
      </div>
    </div>

    <div class="cd-posts" role="list" aria-label="Your posts">
      @for (post of posts; track post.id) {
        <div class="cd-post-card" data-testid="post-card" role="listitem" @fadeIn>
          <div class="cd-post-header">
            <div>
              <div class="cd-post-meta">
                Posted on {{ post.date | date: 'mediumDate' }}
                @if (post.status === 'draft') {
                  <span class="cd-status-badge cd-status-badge--draft" data-testid="post-draft-badge"
                    >Draft</span
                  >
                }
              </div>

              @if (post.postGrades?.length || post.postGroupNames?.length) {
                <div class="cd-audience-row">
                  @if (post.postGrades?.length) {
                    @for (range of formatGradeRanges(post.postGrades); track range) {
                      <span class="cd-audience-badge cd-audience-badge--grade">{{ range }}</span>
                    }
                  }
                  @if (post.postGroupNames?.length) {
                    @for (name of truncateBadges(post.postGroupNames).visible; track name) {
                      <span class="cd-audience-badge cd-audience-badge--group">{{ name }}</span>
                    }
                    @if (truncateBadges(post.postGroupNames).overflow > 0) {
                      <span class="cd-audience-badge cd-audience-badge--overflow"
                        >+{{ truncateBadges(post.postGroupNames).overflow }}</span
                      >
                    }
                  }
                </div>
              }
            </div>
            <button
              class="cd-post-menu-btn"
              [matMenuTriggerFor]="postMenu"
              aria-label="Post options"
              aria-haspopup="true"
            >
              ⋯
            </button>
            <mat-menu #postMenu="matMenu">
              <button mat-menu-item aria-label="Edit this post" (click)="openEditPostDialog(post)">
                Edit
              </button>
              @if (post.status === 'draft') {
                <button
                  mat-menu-item
                  aria-label="Publish this post"
                  data-testid="post-publish-menu-item"
                  (click)="publishPost(post)"
                >
                  Publish
                </button>
              }
              <button mat-menu-item aria-label="Delete this post" (click)="ondeletePost(post)">
                Delete
              </button>
            </mat-menu>
          </div>

          <div class="cd-post-body" data-testid="post-body" [innerHTML]="post.content"></div>
          <app-linked-events [events]="post.linkedEvents" calendarPath="/counselors/calendar" />
          <div class="cd-post-footer">
            <div class="cd-post-stats" aria-label="Post engagement">
              <!-- <span class="cd-stat-pill">
                <span aria-hidden="true">👁</span>
                <span>{{ views }} views</span> -->
              <!-- </span> -->
              <span class="cd-stat-pill">
                <span aria-hidden="true">💬</span>
                <span>{{ post.commentCount }} comments</span>
              </span>
            </div>
            <button
              class="cd-link-btn"
              (click)="toggleComments(post.id)"
              [attr.aria-expanded]="openCommentsPostId === post.id"
              [attr.aria-label]="
                post.commentCount === 0
                  ? 'Start the conversation'
                  : openCommentsPostId === post.id
                    ? 'Hide comments'
                    : 'View ' + post.commentCount + ' comments'
              "
            >
              {{
                post.commentCount === 0
                  ? 'Start the conversation'
                  : openCommentsPostId === post.id
                    ? 'Hide comments'
                    : 'View comments'
              }}
            </button>
          </div>

          @if (openCommentsPostId === post.id) {
            <div
              class="comments-section"
              role="region"
              [attr.aria-label]="'Comments on post from ' + (post.date | date: 'mediumDate')"
            >
              <div class="cd-comments-label" aria-live="polite">
                {{ post.commentCount }} comments
              </div>
              <div class="comment-input-row">
                <mat-form-field class="comment-input" appearance="outline">
                  <input
                    matInput
                    placeholder="Write a comment..."
                    [(ngModel)]="newComment[post.id]"
                    aria-label="Write a comment"
                  />
                </mat-form-field>
                <button
                  mat-icon-button
                  class="comment-submit-btn"
                  (click)="addComment(post.id)"
                  aria-label="Submit comment"
                >
                  <mat-icon aria-hidden="true">arrow_upward</mat-icon>
                </button>
              </div>

              @for (comment of comments[post.id]; track comment.id) {
                <div class="comment">
                  <div class="comment-content">
                    <strong
                      ><app-user-link [userId]="comment.user?.id">{{
                        (comment.user?.preferredName || comment.user?.firstName) +
                          ' ' +
                          comment.user?.lastName
                      }}</app-user-link></strong
                    >
                    @if (comment.user?.id === post.user?.id) {
                      <span class="op-badge" aria-label="Original poster">OP</span>
                    }
                    <p>{{ comment.content }}</p>
                  </div>

                  <button
                    mat-button
                    (click)="toggleReply(comment.id)"
                    [attr.aria-label]="
                      'Reply to ' +
                      (comment.user?.preferredName || comment.user?.firstName) +
                      ' ' +
                      comment.user?.lastName
                    "
                  >
                    Reply
                  </button>

                  @if (activeReplyId === comment.id) {
                    <div class="comment-input-row">
                      <mat-form-field class="comment-input" appearance="outline">
                        <input
                          matInput
                          [(ngModel)]="replyText[comment.id]"
                          placeholder="Write a reply..."
                          [attr.aria-label]="
                            'Write a reply to ' +
                            (comment.user?.preferredName || comment.user?.firstName) +
                            ' ' +
                            comment.user?.lastName
                          "
                        />
                      </mat-form-field>
                      <button
                        mat-icon-button
                        class="comment-submit-btn"
                        (click)="addReply(post.id, comment.id)"
                        aria-label="Submit reply"
                      >
                        <mat-icon aria-hidden="true">arrow_upward</mat-icon>
                      </button>
                    </div>
                  }

                  @if (comment.replies.length) {
                    <div
                      class="replies"
                      role="list"
                      [attr.aria-label]="
                        'Replies to ' +
                        (comment.user?.preferredName || comment.user?.firstName) +
                        ' ' +
                        comment.user?.lastName
                      "
                    >
                      @for (reply of comment.replies; track reply.id) {
                        <div class="reply" role="listitem">
                          <strong
                            ><app-user-link [userId]="reply.user?.id">{{
                              (reply.user?.preferredName || reply.user?.firstName) +
                                ' ' +
                                reply.user?.lastName
                            }}</app-user-link></strong
                          >
                          @if (reply.user?.id === post.user?.id) {
                            <span class="op-badge" aria-label="Original poster">OP</span>
                          }
                          <p>{{ reply.content }}</p>
                        </div>
                      }
                    </div>
                  }
                </div>
              }
            </div>
          }
        </div>
      }
      @if (hasMore) {
        <button class="cd-btn-primary" (click)="loadMore()">Load more</button>
      }
      @if (isLoading) {
        <div class="cd-load-spinner">Loading...</div>
      }

      @if (!hasMore && posts.length) {
        <div class="cd-load-spinner">You're all caught up</div>
      }
    </div>
  </main>
</div>
